import {
  ExpandMore as ExpandMoreIcon,
  PhoneMissed as ContactAttemptIcon,
} from "@mui/icons-material";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { WorkItemList } from "../../../../store/types/work.types";
import { formatLastActionComment } from "../../../../utils/workItemUtils";
import ActivityLink from "../../../activity-link/ActivityLink";
import {
  CONTACT_ATTEMPT_BG,
  CONTACT_ATTEMPT_FG,
  INK_3,
  PAPER,
  formatDateShort,
  formatDateTime,
} from "./types";

interface ContactAttemptGroupProps {
  items: WorkItemList[];
  customerNo: number;
  /** Sub-läge: renderas inuti en ärende-lane med vänster tidslinje-räls. */
  variant?: "sub" | "free";
}

const ts = (s: string | null | undefined) => (s ? new Date(s).getTime() : 0);

const AttemptRow = ({
  item,
  customerNo,
  showLabel = true,
}: {
  item: WorkItemList;
  customerNo: number;
  showLabel?: boolean;
}) => {
  const comment = formatLastActionComment(item.Result);
  return (
    <ActivityLink customerNo={customerNo} workItemNo={item.Id}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          py: 0.4,
          px: 0.5,
          borderRadius: "6px",
          "&:hover": { bgcolor: "rgba(0,0,0,.03)" },
        }}
      >
        <ContactAttemptIcon sx={{ fontSize: 13, color: CONTACT_ATTEMPT_FG }} />
        {showLabel && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: CONTACT_ATTEMPT_FG,
              whiteSpace: "nowrap",
            }}
          >
            Kontaktförsök
          </Typography>
        )}
        <Typography sx={{ fontSize: 12, color: INK_3, whiteSpace: "nowrap" }}>
          {formatDateTime(item.ScheduledTo)}
        </Typography>
        {comment && (
          <Typography
            sx={{
              fontSize: 12,
              color: INK_3,
              fontStyle: "italic",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            · {comment}
          </Typography>
        )}
      </Stack>
    </ActivityLink>
  );
};

const ContactAttemptGroup = ({
  items,
  customerNo,
  variant = "sub",
}: ContactAttemptGroupProps) => {
  const [open, setOpen] = useState(false);
  const single = items.length === 1;

  // Datumspann för sammanfattningen (äldsta–nyaste).
  const sorted = [...items].sort(
    (a, b) => ts(a.ScheduledTo) - ts(b.ScheduledTo)
  );
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const firstLabel = formatDateShort(first.ScheduledTo);
  const lastLabel = formatDateShort(last.ScheduledTo);
  const rangeLabel =
    firstLabel === lastLabel ? firstLabel : `${firstLabel}–${lastLabel}`;

  const dotOffset = variant === "sub" ? { ml: 0 } : {};

  // Enstaka kontaktförsök → en enda minimal rad.
  if (single) {
    return (
      <Box sx={{ position: "relative", py: 0.5, ...dotOffset }}>
        {variant === "sub" && (
          <Box
            sx={{
              position: "absolute",
              left: -19,
              top: 12,
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: CONTACT_ATTEMPT_FG,
              border: `2px solid ${PAPER}`,
            }}
          />
        )}
        <AttemptRow item={first} customerNo={customerNo} />
      </Box>
    );
  }

  // Flera → hopfällbar sammanfattning.
  return (
    <Box sx={{ position: "relative", py: 0.5 }}>
      {variant === "sub" && (
        <Box
          sx={{
            position: "absolute",
            left: -20,
            top: 11,
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: CONTACT_ATTEMPT_FG,
            border: `2px solid ${PAPER}`,
            boxShadow: `0 0 0 2px ${CONTACT_ATTEMPT_BG}`,
          }}
        />
      )}
      <Stack
        direction="row"
        alignItems="center"
        gap={0.85}
        onClick={() => setOpen((o) => !o)}
        sx={{
          py: 0.5,
          px: 0.85,
          borderRadius: "8px",
          cursor: "pointer",
          bgcolor: CONTACT_ATTEMPT_BG,
          "&:hover": { filter: "brightness(0.97)" },
        }}
      >
        <ContactAttemptIcon sx={{ fontSize: 14, color: CONTACT_ATTEMPT_FG }} />
        <Typography
          sx={{ fontSize: 12.5, fontWeight: 600, color: CONTACT_ATTEMPT_FG }}
        >
          {items.length} kontaktförsök
        </Typography>
        <Typography sx={{ fontSize: 12, color: CONTACT_ATTEMPT_FG, opacity: 0.8 }}>
          · {rangeLabel}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: CONTACT_ATTEMPT_FG,
            transition: "transform .2s",
            transform: open ? "rotate(180deg)" : "rotate(0)",
          }}
        />
      </Stack>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 1, pt: 0.25 }}>
          {sorted
            .slice()
            .reverse()
            .map((item) => (
              <AttemptRow
                key={item.Id}
                item={item}
                customerNo={customerNo}
                showLabel={false}
              />
            ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ContactAttemptGroup;
